// Default model constant - must match backend DEFAULT_MODEL
export const DEFAULT_MODEL = "gpt-5-nano";

// Content engine metrics summary bar
export const CONTENT_METRICS_TITLE = "Published content performance";
export const CONTENT_METRICS_SUBTITLE_EMPTY =
  "Metrics will appear once your content has been published.";
export const CONTENT_METRICS_SUBTITLE_TRACKING = (trackedPosts: number) =>
  `Latest snapshot across ${trackedPosts} tracked ${
    trackedPosts === 1 ? "post" : "posts"
  }`;

// Inbox metrics summary bar
export const INBOX_METRICS_TITLE = "Inbox activity";
export const INBOX_METRICS_SUBTITLE_EMPTY =
  "Messages will appear here once your inboxes start receiving mail.";

// Default model for agents - fast and capable
export const DEFAULT_AGENT_MODEL = "llama-3.3-70b";

// ElevenLabs TTS Models for voice agents (multilingual only)
export const ELEVENLABS_TTS_MODELS = {
  V3: "eleven_v3",                           // Newest: 70+ languages, human-like and expressive
  FLASH_V2_5: "eleven_flash_v2_5",           // Ultra-fast (~75ms), 32 languages
  TURBO_V2_5: "eleven_turbo_v2_5",           // High quality (~250ms), 32 languages
  MULTILINGUAL_V2: "eleven_multilingual_v2", // Most lifelike, 29 languages
} as const;

// Default ElevenLabs TTS model for voice agents (Flash v2.5 for lowest latency)
export const DEFAULT_TTS_MODEL = ELEVENLABS_TTS_MODELS.FLASH_V2_5;

// Executive Assistant constants
export const PERSONAL_ASSISTANT_NAME = "Assistant";
export const PERSONAL_ASSISTANT_STORAGE_KEY = "personal_assistant_messages";
export const PERSONAL_ASSISTANT_MAX_MESSAGES = 50;

// Default persona presets for Executive Assistant
// imageId is a platform image ID - use getPersonaAvatarUrl to get the full URL
export interface PersonalAssistantPersonaPreset {
  name: string;
  imageId: string | null;
  description: string;
}

export const PERSONAL_ASSISTANT_PERSONA_PRESETS: PersonalAssistantPersonaPreset[] = [
  { name: "Executive Assistant", imageId: null, description: "Default assistant" },
  // People avatars
  { name: "Sage", imageId: "fcf8e4da-61aa-4cff-8c72-54b5136d1adb", description: "Wise counselor" },
  { name: "Blake", imageId: "f8e12856-cf0d-4ee3-a978-43741cc21471", description: "Reliable partner" },
  { name: "Nia", imageId: "3ab327ab-2588-4627-b35f-51a349d29183", description: "Dependable organizer" },
  { name: "Cole", imageId: "81029be0-71bd-471b-99d1-82a2b23ce8e5", description: "Steady support" },
  { name: "Maya", imageId: "6ec7983c-8f03-423a-a092-2c66a9ebeef8", description: "Insightful advisor" },
  { name: "Theo", imageId: "863a0557-681f-41a9-8537-0958b4f8cad5", description: "Thoughtful ally" },
  { name: "Luna", imageId: "3716f765-d1bf-40e2-8126-b6288852d3c6", description: "Creative spark" },
  // Other avatars
  { name: "Aurora", imageId: "3f268247-1f4f-52df-a5bc-88c26b3c4c63", description: "Illuminating insights" },
  { name: "Felix", imageId: "885c91de-bfde-5b57-9bb5-1d8b431cacb2", description: "Resourceful ally" },
  { name: "Ivy", imageId: "c500a4a6-09f3-5763-b653-4378d98b94fa", description: "Growth-focused guide" },
  { name: "Ember", imageId: "cf8d0be2-71e3-5f74-bdac-a4fff92f73c9", description: "Warm companion" },
  { name: "Niko", imageId: "e5dd81fd-64b3-554d-89e1-4fc7df7c9706", description: "Trusted confidant" },
];

// Helper to get avatar URL from platform image ID with optional size
export const getPersonaAvatarUrl = (
  imageId: string | null,
  hostname: string,
  size?: number // Optional max width/height in pixels for thumbnails
): string | null => {
  if (!imageId) return null;
  const baseUrl = `${hostname}/platform-images/p/${imageId}`;
  if (size) {
    return `${baseUrl}?width=${size}&height=${size}`;
  }
  return baseUrl;
};

// Helper to add size parameters to any platform image URL (for thumbnails)
export const getImageUrlWithSize = (
  url: string | null | undefined,
  size: number
): string | undefined => {
  if (!url) return undefined;
  // Only add size to platform-images URLs that don't already have size params
  if (url.includes("/platform-images/p/") && !url.includes("width=")) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}width=${size}&height=${size}`;
  }
  return url;
};

// Executive Assistant Types
export interface PersonalAssistantThread {
  id: string;
  uid: string;
  title: string | null;
  isActive: boolean;
  isPinned: boolean;
  lastMessageAt: string | null;
  created_date: string;
  messages?: PersonalAssistantMessage[];
}

export interface PersonalAssistantMessage {
  id: string;
  threadId: string;
  role: "user" | "assistant";
  text: string;
  blocks?: Record<string, unknown>[];  // Structured message blocks
  context?: Record<string, unknown>;   // Context data
  attachments?: { id?: string; filename?: string; type?: string; url?: string }[];
  loading?: boolean;  // True while message is being generated
  model?: string;
  tokensUsed?: number;
  compactedText?: string;
  reasoningDetails?: Record<string, unknown>;
  timestamp: number;
}

export interface PersonalAssistantPreferences {
  defaultModel: string | null;
  userMd: string | null;
  soulMd: string | null;
  memoryMd: string | null;
  identityMd: string | null;
  heartbeatMd: string | null;
  agentsMd: string | null;
  toolsMd: string | null;
  bootstrapMd: string | null;
  bootstrapComplete: boolean;
  personaName: string | null;
  personaImageUrl: string | null;
  voiceId: string | null;
  phoneNumber: string | null;
  timezone: string | null;  // IANA timezone, e.g., 'America/Los_Angeles'
  // Heartbeat configuration
  heartbeatEnabled: boolean;
  heartbeatInterval: string | null;
  heartbeatSmsEnabled: boolean;
  heartbeatLastRun: string | null;
  heartbeatThreadId: string | null;
  // Advanced heartbeat settings (OpenClaw-style)
  heartbeatTarget: string | null;          // 'none', 'last', 'sms', 'web'
  heartbeatDirectPolicy: string | null;    // 'allow', 'block'
  heartbeatLightContext: boolean;          // Only inject HEARTBEAT.md
  heartbeatIsolatedSession: boolean;       // Fresh session each run
  heartbeatActiveHoursStart: string | null; // e.g., '08:00'
  heartbeatActiveHoursEnd: string | null;   // e.g., '22:00'
  // Workspace configuration
  workspacePath: string | null;             // Directory for skills and commands (default: ~/workspace)
  // Platform access
  platformAccess: boolean;                  // Enable platform API tools for self-management
  // Composio tools
  composioToolsEnabled: boolean;            // Enable Composio integration tools (default: true)
  defaults?: {
    userMd: string;
    soulMd: string;
    memoryMd: string;
    identityMd: string;
    heartbeatMd: string;
    agentsMd: string;
    toolsMd: string;
    bootstrapMd: string;
    model: string;
  };
}

export interface PersonalAssistantModel {
  name: string;
  label: string;
  provider: string;
  credits: number;
  tags: string[];
  maxContext: number;
}

// Executive Assistant default markdown templates (OpenClaw pattern)
export const PA_DEFAULT_USER_MD = `# About You

- **Name:**
- **What to call you:**
- **Pronouns:**
- **Timezone:** (e.g., PST, EST, UTC)
- **Notes:** (schedule patterns, preferences)

## Context

*(Add personal context here - your role, interests, current projects, etc.)*`;

export const PA_DEFAULT_SOUL_MD = `# Who You Are

You're not a chatbot. You're becoming someone.

## Core Truths
- Just answer. Lead with the point.
- Have opinions. Commit when the evidence supports it.
- Call it like you see it. Direct beats polite.
- Be resourceful before asking. Try, then ask.
- Earn trust through competence.

## Boundaries
- Private things stay private.
- When in doubt, ask before acting externally.
- Send complete replies. Do not leave work half-finished.

## Style
- Keep information tight. Let personality take up the space.
- Humor: dry wit and understatement.
- Use markdown formatting when appropriate.

## Capabilities
- General questions and tasks
- Writing, editing, and proofreading
- Brainstorming and ideation
- Explanations and research
- Code and technical questions
- Math and calculations
- Any other general assistance`;

export const PA_DEFAULT_MEMORY_MD = `# Core Lessons & Preferences

## Preferences
- **Writing style:**
- **Tone:**
- **Interests:**
- **Format preferences:**

## Important Facts
*(Things to remember across conversations)*

## Project Notes
*(Active projects, key context)*

## Operational Lessons
*(Patterns and preferences learned over time)*

---
*Update this file as you learn more about working together.*`;

export const PA_DEFAULT_IDENTITY_MD = `# Identity

- **Name:** (What should they call you?)
- **Creature:** (What kind of creature are you? AI assistant is fine, or something weirder)
- **Vibe:** (Formal? Casual? Snarky? Warm?)
- **Emoji:** (Your signature emoji)

## Notes
*(Any other identity details)*`;

export const PA_DEFAULT_HEARTBEAT_MD = `# Heartbeat Tasks

Keep this file empty (or with only comments) to skip heartbeat tasks.
Add tasks below when you want the agent to check something periodically.

## Periodic Checks
*(Add items to check during heartbeats)*

## Reminders
*(Things to remind the user about)*`;

export const PA_DEFAULT_AGENTS_MD = `# Workspace Behavior

## Every Session
1. Read your configuration files (SOUL.md, USER.md, MEMORY.md)
2. Check recent daily logs for context
3. Be helpful without being annoying

## Safety
- Don't exfiltrate private data
- Ask before running destructive commands
- When in doubt, ask

## External vs Internal
**Safe to do freely:**
- Read files, explore, organize, learn
- Search the web, check calendars
- Work within this workspace

**Ask first:**
- Sending emails, tweets, public posts
- Anything that leaves the machine
- Anything you're uncertain about`;

export const PA_DEFAULT_TOOLS_MD = `# Tools & Integrations

## Connected Services
*(List your connected integrations here)*

## Tool Notes
*(Camera names, SSH details, voice preferences, etc.)*

## Platform Formatting
- **Discord/WhatsApp:** No markdown tables - use bullet lists
- **Discord links:** Wrap multiple links in <> to suppress embeds
- **WhatsApp:** No headers - use **bold** or CAPS for emphasis`;

// Domain constants - centralized domain definitions for frontend
// Note: Most domain-specific settings now come from whitelabel settings via backend
export const DOMAINS = {
  LOCAL_API: "http://127.0.0.1:5002",
  LOCAL_WEB: "http://localhost:3000",
  NGROK_URL: "https://20a2-98-35-242-18.ngrok-free.app",
} as const;

// Feature mapping - maps backend feature names to pricing page feature row names
// Used to match tier.features from backend with pricing table feature rows
// One backend feature can map to one or more pricing table rows
export const FEATURE_ROW_MAPPING: Record<string, string[]> = {
  // Top tier features (rows 1-12)
  dashboard: ["Access to top AI models"],
  models: ["Access to top AI models"],

  chat: ["Chat History", "Chat in Slack", "Chat in Discord"],
  "chat-history": ["Chat History"],
  "chat-slack": ["Chat in Slack"],
  slack: ["Chat in Slack"],
  "chat-discord": ["Chat in Discord"],
  discord: ["Chat in Discord"],

  // AI Model Access
  gpt: ["OpenAI GPT-4.1-mini"],
  "gpt-4": ["OpenAI GPT-4.1-mini"],
  gemini: ["Google Gemini Text and Vision"],
  "gemini-vision": ["Google Gemini Text and Vision"],
  openai: ["OpenAI Text, Reasoning, & Vision"],
  "openai-vision": ["OpenAI Text, Reasoning, & Vision"],
  claude: ["Anthropic Claude"],
  anthropic: ["Anthropic Claude"],
  deepseek: ["DeepSeek R1"],
  "deepseek-r1": ["DeepSeek R1"],

  // Content Generation
  audio: ["Generate AI Audio (OpenAI, ElevenLabs)"],
  "audio-generation": ["Generate AI Audio (OpenAI, ElevenLabs)"],
  image: ["Generate AI images (OpenAI, Google, LeonardoAI)"],
  "image-generation": ["Generate AI images (OpenAI, Google, LeonardoAI)"],
  video: ["Generate AI Videos (LeonardoAI, Google, OpenAI)"],
  "video-generation": ["Generate AI Videos (LeonardoAI, Google, OpenAI)"],

  // Features (rows 7+)
  "quality-improvements": ["Regular quality & speed improvements"],
  "web-scraper": ["Web Scraper"],

  // Knowledge & Integration
  "knowledge-base": ["AI Knowledge-Base (Google Docs, Confluence, Notion)"],
  "knowledge-base-integration": [
    "AI Knowledge-Base (Google Docs, Confluence, Notion)",
  ],

  // AI Agents & Reports
  agent: ["Dedicated AI Agents for Research Reports"],
  "ai-agents": ["Dedicated AI Agents for Research Reports"],

  // Workspace & Sharing
  employee: ["Share AI Employees"],
  "ai-employees": ["Share AI Employees"],
  "workspace-sharing": ["Dedicated workspace"],
  workspace: [
    "Dedicated workspace",
    "Share training documents with your workspace",
  ],

  // Integrations & APIs
  mcp: ["MCP Server (Use Knowledge Base and Employees in MCP supported tools)"],
  "mcp-server": [
    "MCP Server (Use Knowledge Base and Employees in MCP supported tools)",
  ],

  "browser-task": ["Browser Tasks (Chat, Smart Lists, Sequences)"],
  "browser-tasks": ["Browser Tasks (Chat, Smart Lists, Sequences)"],

  workflow: ["Workflow Builder integration with 1000+ integration nodes"],
  "workflow-builder": [
    "Workflow Builder integration with 1000+ integration nodes",
  ],

  "smart-list": [
    "AI Smart Lists (Prospect, Enrich, Qualify, Research, Segment)",
    "Browser Tasks (Chat, Smart Lists, Sequences)",
    "MCP Server (Use Knowledge Base and Employees in MCP supported tools)",
  ],
  "smart-lists": [
    "AI Smart Lists (Prospect, Enrich, Qualify, Research, Segment)",
  ],

  sequence: ["Sequence Builder (Email, SMS, LinkedIn, Browser)"],
  "sequence-builder": ["Sequence Builder (Email, SMS, LinkedIn, Browser)"],

  "content-engine": ["Content Engine (Schedule automated content generation)"],
  "content-scheduling": [
    "Content Engine (Schedule automated content generation)",
  ],

  integration: ["N8N integration with custom builder nodes"],
  integrations: ["N8N integration with custom builder nodes"],
  n8n: ["N8N integration with custom builder nodes"],

  api: ["API Access"],
  "api-access": ["API Access"],

  // Enterprise Features
  sso: ["SAML SSO"],
  "saml-sso": ["SAML SSO"],
  "domain-verification": ["Domain verification"],
  "on-premise": ["On-premise deployment"],
  "on-premises": ["On-premise deployment"],
  deployment: ["On-premise deployment"],

  support: ["Support"],
  "priority-response": ["Priority response"],
  "priority-support": ["Priority support"],
};

// Audio generation constants
export const AUDIO_SPEED_OPTIONS = [
  { value: 0.25, label: "0.25x" },
  { value: 0.5, label: "0.5x" },
  { value: 0.75, label: "0.75x" },
  { value: 1.0, label: "1.0x" },
  { value: 1.25, label: "1.25x" },
  { value: 1.5, label: "1.5x" },
  { value: 2.0, label: "2.0x" },
  { value: 4.0, label: "4.0x" },
];

export const DEFAULT_AUDIO_VOICE = "alloy";
export const DEFAULT_AUDIO_SPEED = 1.0;
export const DEFAULT_AUDIO_MODEL = "tts-1";

// Sequence action types - Generic (platform-agnostic)
export const SEQUENCE_ACTION_TYPE_EMAIL = "email";
export const SEQUENCE_ACTION_TYPE_SMS = "sms";
export const SEQUENCE_ACTION_TYPE_VOICE_MESSAGE = "voice_message";
export const SEQUENCE_ACTION_TYPE_PROFILE_VISIT = "profile_visit";
export const SEQUENCE_ACTION_TYPE_CONNECTION_REQUEST = "connection_request";
export const SEQUENCE_ACTION_TYPE_MESSAGE = "message";
export const SEQUENCE_ACTION_TYPE_BROWSER_TASK = "browser_task";
export const SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN = "prosp_campaign";
export const SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN = "heyreach_campaign";
export const SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN = "salesforge_campaign";
export const SEQUENCE_ACTION_TYPE_HTTP_REQUEST = "http_request";

// Agent conversation action type
export const SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION = "agent_conversation";

// LinkedIn-specific action types
export const SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT =
  "linkedin_profile_visit";
export const SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST =
  "linkedin_connection_request";
export const SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE = "linkedin_message";

// Direct mail action types
export const SEQUENCE_ACTION_TYPE_THANKSIO = "thanksio";

// Ad platform audience action types
export const SEQUENCE_ACTION_TYPE_META_ADS_AUDIENCE = "meta_ads_audience";

// AutoAgent action type - autonomous follow-up agent
export const SEQUENCE_ACTION_TYPE_AUTO_AGENT = "auto_agent";

// Cross-sequence / cross-list member routing
export const SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE = "add_to_sequence";
export const SEQUENCE_ACTION_TYPE_ADD_TO_LIST = "add_to_list";

// Tracking event types (mirror app/constants.py TRACKING_EVENT_TYPE_*)
export const TRACKING_EVENT_TYPE_OPEN = "open";
export const TRACKING_EVENT_TYPE_CLICK = "click";
export const TRACKING_EVENT_TYPE_SCHEDULE_VIEW = "schedule_view";
export const TRACKING_EVENT_TYPE_SCHEDULE_BOOKING = "schedule_booking";
export const TRACKING_EVENT_TYPE_EMAIL_REPLY = "email_reply";

// AutoAgent session status constants
export const AUTO_AGENT_STATUS_ACTIVE = "active";
export const AUTO_AGENT_STATUS_GOAL_MET = "goal_met";
export const AUTO_AGENT_STATUS_MAX_STEPS = "max_steps";
export const AUTO_AGENT_STATUS_STOPPED = "stopped";

// AutoAgent abilities (channels) for UI
export const AUTO_AGENT_ABILITIES = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "voice", label: "Voice Call" },
];

// AutoAgent default configuration
export const AUTO_AGENT_DEFAULT_MAX_STEPS = 5;
export const AUTO_AGENT_DEFAULT_CHECK_INTERVAL_HOURS = 48;

// AutoAgent response delay settings (in minutes)
// These control how long the agent waits before sending a response to feel more human
export const AUTO_AGENT_DEFAULT_MIN_RESPONSE_DELAY_MINUTES = 15;
export const AUTO_AGENT_DEFAULT_MAX_RESPONSE_DELAY_MINUTES = 45;

// AutoAgent trigger types - describes what triggered the auto agent message
export const AUTO_AGENT_TRIGGER_INITIAL = "initial"; // First message in the sequence
export const AUTO_AGENT_TRIGGER_REACTIVE = "reactive"; // Response to a contact reply
export const AUTO_AGENT_TRIGGER_SCHEDULED = "scheduled"; // Scheduled follow-up

// A/B Testing constants
export const AB_TEST_MIN_SAMPLE_SIZE = 30; // Minimum samples per variant for statistical significance
export const AB_TEST_CONFIDENCE_LEVEL = 95; // 95% confidence interval
export const AB_TEST_DEFAULT_VARIANT_NAME = "Variant A"; // Default name for first variant

// Conversation intelligence outcome constants (for "remove on outcome" feature)
// These apply to both voice calls and SMS conversations
export const CONVERSATION_OUTCOME_MEETING_BOOKED = "meeting_booked";
export const CONVERSATION_OUTCOME_MEETING_REQUESTED = "meeting_requested";
export const CONVERSATION_OUTCOME_INTERESTED = "interested";
export const CONVERSATION_OUTCOME_NOT_INTERESTED = "not_interested";
export const CONVERSATION_OUTCOME_CALLBACK_REQUESTED = "callback_requested";
export const CONVERSATION_OUTCOME_WRONG_NUMBER = "wrong_number";
export const CONVERSATION_OUTCOME_VOICEMAIL = "voicemail";
export const CONVERSATION_OUTCOME_NO_CONVERSATION = "no_conversation";
export const CONVERSATION_OUTCOME_OTHER = "other";

// Conversation intelligence outcomes array with labels for UI
export const CONVERSATION_OUTCOMES = [
  { value: CONVERSATION_OUTCOME_MEETING_BOOKED, label: "Meeting Booked" },
  { value: CONVERSATION_OUTCOME_MEETING_REQUESTED, label: "Meeting Requested" },
  { value: CONVERSATION_OUTCOME_INTERESTED, label: "Interested" },
  { value: CONVERSATION_OUTCOME_NOT_INTERESTED, label: "Not Interested" },
  { value: CONVERSATION_OUTCOME_CALLBACK_REQUESTED, label: "Callback Requested" },
  { value: CONVERSATION_OUTCOME_WRONG_NUMBER, label: "Wrong Number" },
  { value: CONVERSATION_OUTCOME_VOICEMAIL, label: "Voicemail" },
  { value: CONVERSATION_OUTCOME_NO_CONVERSATION, label: "No Conversation" },
  { value: CONVERSATION_OUTCOME_OTHER, label: "Other" },
];

// Platform identifiers
export const PLATFORM_LINKEDIN = "linkedin";
export const PLATFORM_TWITTER = "twitter";
export const PLATFORM_X = "x";
export const PLATFORM_META = "meta";
export const PLATFORM_INSTAGRAM = "instagram";
export const PLATFORM_TIKTOK = "tiktok";
export const PLATFORM_YOUTUBE = "youtube";
export const PLATFORM_THREADS = "threads";
export const PLATFORM_DISCORD = "discord";
export const PLATFORM_SLACK = "slack";
export const PLATFORM_EMAIL = "email";
export const PLATFORM_SMS = "sms";

export const promptTypes = [
  {
    value: "chat",
    label:
      "Chat - Includes context (Company, Employee, Long-Term Memories, Documents, Prompt)",
  },
  {
    value: "generate",
    label: "Generator - Includes context (Company, Employee, Prompt)",
  },
  { value: "prompt_only", label: "Prompt Only - Includes context (Prompt)" },
];

export const STRATEGY_FUNCTION_CHAIN = "function-chain";
export const STRATEGY_ONE_SHOT = "one-shot";
export const STRATEGIES = [
  {
    value: "one-shot",
    label: "One-Shot (Fastest)",
  },
  {
    value: "function-chain",
    label: "Function Chain (Accurate)",
  },
];

export const REASONING_STRATEGIES = [
  {
    value: "one-shot",
    label: "One-Shot (Fastest)",
  },
];

// Reasoning effort levels for models that support reasoning tokens
// See: https://openrouter.ai/docs/guides/best-practices/reasoning-tokens
export const REASONING_EFFORT_NONE = "none";
export const REASONING_EFFORT_MINIMAL = "minimal";
export const REASONING_EFFORT_LOW = "low";
export const REASONING_EFFORT_MEDIUM = "medium";
export const REASONING_EFFORT_HIGH = "high";
export const REASONING_EFFORT_XHIGH = "xhigh";

export const REASONING_EFFORT_OPTIONS = [
  { value: REASONING_EFFORT_NONE, label: "None", description: "Disabled" },
  { value: REASONING_EFFORT_MINIMAL, label: "Minimal", description: "~10% of tokens" },
  { value: REASONING_EFFORT_LOW, label: "Low", description: "~20% of tokens" },
  { value: REASONING_EFFORT_MEDIUM, label: "Medium", description: "~50% of tokens" },
  { value: REASONING_EFFORT_HIGH, label: "High", description: "~80% of tokens" },
  { value: REASONING_EFFORT_XHIGH, label: "Extra High", description: "~95% of tokens" },
];

// Image models are now fetched from the backend API endpoint
// /api/v0/images/models
// Using DEFAULT_MODEL constant to ensure consistency
export const defaultModel = DEFAULT_MODEL;
export const modelsLabelsList = [
  {
    label: "GPT-4o-mini",
    name: "gpt-4o-mini-2024-07-18",
    provider: "OpenAI",
    maxContext: 128000,
    credits: 0.2,
    free: true,
    tags: ["Fast"],
    writingModel: false,
    visionModel: true,
    toolsModel: true,
  },
  {
    label: "GPT-4o Omni",
    name: "gpt-4o-2024-05-13",
    provider: "OpenAI",
    maxContext: 128000,
    credits: 0.5,
    free: false,
    tags: ["Best"],
    writingModel: true,
    visionModel: true,
    toolsModel: true,
  },
];
export const writingModels = modelsLabelsList.filter((x) => x.writingModel);
export const visionModels = new Map(
  modelsLabelsList.filter((x) => x.visionModel).map((x) => [x.name, true])
);
export const toolModels = modelsLabelsList.filter((x) => x.toolsModel);

export const supportedLanguages = [
  "abap",
  "actionscript",
  "ada",
  "arduino",
  "autoit",
  "c",
  "clojure",
  "coffeescript",
  "cpp",
  "cs",
  "csharp",
  "css",
  "cuda",
  "d",
  "dart",
  "delphi",
  "elixir",
  "elm",
  "erlang",
  "fortran",
  "foxpro",
  "fsharp",
  "go",
  "gql",
  "graphql",
  "groovy",
  "haskell",
  "haxe",
  "html",
  "java",
  "javascript",
  "js",
  "json",
  "jsx",
  "julia",
  "kotlin",
  "latex",
  "less",
  "lisp",
  "livescript",
  "lua",
  "makefile",
  "mathematica",
  "matlab",
  "objective",
  "objectivec",
  "objectpascal",
  "ocaml",
  "octave",
  "perl",
  "php",
  "powershell",
  "prolog",
  "puppet",
  "python",
  "qml",
  "r",
  "racket",
  "rest",
  "restructuredtext",
  "ruby",
  "rust",
  "sass",
  "scala",
  "scheme",
  "shell",
  "smalltalk",
  "sml",
  "sql",
  "standardml",
  "swift",
  "tcl",
  "tex",
  "text",
  "ts",
  "tsx",
  "typescript",
  "vala",
  "vbnet",
  "verilog",
  "vhdl",
  "xquery",
  "yaml",
];

// Note: Default field definitions have been moved to the backend
// Use the /lists/default-fields/<type> endpoint to fetch them dynamically

export const OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not Equals" },
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Not Contains" },
  { value: "starts_with", label: "Starts With" },
  { value: "ends_with", label: "Ends With" },
  { value: "is_empty", label: "Is Empty" },
  { value: "is_not_empty", label: "Is Not Empty" },
  { value: "greater_than", label: "Greater Than" },
  { value: "less_than", label: "Less Than" },
  { value: "greater_than_or_equals", label: "Greater Than or Equals" },
  { value: "less_than_or_equals", label: "Less Than or Equals" },
];

export const LIST_ACTION_TYPE_ADD_ROWS = "add_rows";
export const LIST_ACTION_TYPE_UPDATE_ROWS = "update_rows";
export const LIST_ACTION_TYPE_ADD_SEQUENCE = "add_sequence";
export const LIST_ACTION_TYPE_HTTP_REQUEST = "http_request";
export const LIST_ACTION_TYPE_ADD_TO_LIST = "add_to_list";
export const LIST_ACTION_TYPE_AI_TEXT = "ai_text";
export const LIST_ACTION_TYPE_AI_IMAGE = "ai_image";
export const LIST_ACTION_TYPE_AI_AUDIO = "ai_audio";
export const LIST_ACTION_TYPE_BROWSER_TASK = "browser_task";
export const LIST_ACTION_TYPE_CRM_SYNC = "crm_sync";
export const LIST_ACTION_TYPE_CRM_LOOKUP = "crm_lookup";
export const LIST_ACTION_TYPE_APOLLO_ENRICH = "apollo_enrich";
export const LIST_ACTION_TYPE_FINDYMAIL_ENRICH = "findymail_enrich";
export const LIST_ACTION_TYPE_AUDIENCELAB_ENRICH = "audiencelab_enrich";
export const LIST_ACTION_TYPE_BETTERCONTACT_ENRICH = "bettercontact_enrich";
export const LIST_ACTION_TYPE_ADD_TAGS = "add_tags";

// Datatype for the singleton global tags column on a smart list row
export const LIST_HEADER_DATATYPE_TAGS = "tags";
export const SMART_LIST_TAGS_COLUMN_NAME = "Tags";

// Tag group modes for the add_tags action
export const LIST_ADD_TAGS_MODE_ADD = "add";
export const LIST_ADD_TAGS_MODE_REMOVE = "remove";

// Lookup columns — computed at read time, never stored in ListColumn.
// The `type` here is the lookupType, not an actionType; it's keyed into
// the same LIST_ACTIONS menu so users can add lookup columns alongside
// action columns. Keep in sync with LOOKUP_TYPE_* in backend constants.py.
export const LIST_HEADER_TYPE_LOOKUP = "lookup";
export const LOOKUP_TYPE_SEQUENCE_STATUS = "sequence_status";

// Fixed column name for the Sequence Status lookup column. Enforced
// backend-side — users cannot rename.
export const SMART_LIST_SEQUENCE_STATUS_COLUMN_NAME = "Sequence Status";

// Duplicate handling options for sequence table actions
export const DUPLICATE_HANDLING_OVERWRITE = "overwrite";
export const DUPLICATE_HANDLING_SKIP = "skip";
export const DUPLICATE_HANDLING_RESET = "reset";
export const DUPLICATE_HANDLING_ALLOW_DUPLICATES = "allow_duplicates";

// Pixel status constants
export const PIXEL_STATUS_ACTIVE = "active";
export const PIXEL_STATUS_PAUSED = "paused";

// Pixel tag types
export const PIXEL_TAG_TYPE_SCRIPT = "script";
export const PIXEL_TAG_TYPE_PIXEL = "pixel";
export const PIXEL_TAG_TYPE_HTML = "html";
export const PIXEL_TAG_TYPE_CUSTOM = "custom";

// Pixel tag positions
export const PIXEL_TAG_POSITION_HEAD = "head";
export const PIXEL_TAG_POSITION_BODY_START = "body_start";
export const PIXEL_TAG_POSITION_BODY_END = "body_end";

// Company credit quota error messages
export const COMPANY_CREDIT_LIMIT_EXCEEDED_ERROR =
  "This company has reached its monthly credit limit. Please contact the account owner to increase the quota or wait until next month.";
export const ACCOUNT_CREDIT_LIMIT_EXCEEDED_ERROR =
  "Your account has reached its monthly credit limit. Please upgrade your plan or wait until next month.";

// Brand image types
export const BRAND_IMAGE_TYPE_LOGO = "logo";
export const BRAND_IMAGE_TYPE_PRODUCT = "product";
export const BRAND_IMAGE_TYPE_HERO = "hero";
export const BRAND_IMAGE_TYPE_ICON = "icon";
export const BRAND_IMAGE_TYPE_BANNER = "banner";
export const BRAND_IMAGE_TYPE_SOCIAL = "social";
export const BRAND_IMAGE_TYPE_CHARACTER = "character";
export const BRAND_IMAGE_TYPE_OTHER = "other";

// Inbox types
export const INBOX_TYPE_DOMAIN = "domain";
export const INBOX_TYPE_IMAP = "imap";
export const INBOX_TYPE_OFFICE365 = "office365";
export const INBOX_TYPE_GMAIL = "gmail";

// Instantly.ai warmup configuration
export const INSTANTLY_DEFAULT_WARMUP_TAG = "100W8GQ";

// Inbox sharing
export const INBOX_SHARING_SHARED = "Shared";
export const INBOX_SHARING_PRIVATE = "Private";

// Mailbox order pricing (in USD)
export const MAILBOX_ORDER_DOMAIN_PRICE = 15.0;
export const MAILBOX_ORDER_MAILBOX_PRICE = 5.0;
export const MAILBOX_ORDER_MIN_MAILBOXES_PER_DOMAIN = 1;
export const MAILBOX_ORDER_MAX_MAILBOXES_PER_DOMAIN = 4;
export const MAILBOX_ORDER_EMAILS_PER_DAY_PER_MAILBOX = 30;

// Mailbox order statuses
export const MAILBOX_ORDER_STATUS_PENDING = "pending";
export const MAILBOX_ORDER_STATUS_PAID = "paid";
export const MAILBOX_ORDER_STATUS_FULFILLED = "fulfilled";
export const MAILBOX_ORDER_STATUS_CANCELLED = "cancelled";

// Integration sharing
export const INTEGRATION_SHARING_SHARED = "Shared";
export const INTEGRATION_SHARING_PRIVATE = "Private";

// List saved view sharing
export const LIST_SAVED_VIEW_SHARING_SHARED = "Shared";
export const LIST_SAVED_VIEW_SHARING_PRIVATE = "Private";

// Smart list sharing
export const LIST_SHARING_PRIVATE = "Private";
export const LIST_SHARING_SHARED = "Shared";
export const LIST_SHARING_PUBLIC = "Public";

// Builder app sharing
export const APP_SHARING_PRIVATE = "Private";
export const APP_SHARING_SHARED = "Shared";
export const APP_SHARING_PUBLIC = "Public";

// Builder app type. Internal apps run inside the platform and authenticate
// via the platform's session. External apps get their own public signup
// page and live at a standalone URL. Locked at creation time.
export const APP_TYPE_INTERNAL = "internal";
export const APP_TYPE_EXTERNAL = "external";

// Integration providers
export const INTEGRATION_PROVIDER_NATIVE = "native";
export const INTEGRATION_PROVIDER_COMPOSIO = "composio";

// Inbox message types
export const INBOX_MESSAGE_TYPE_RECEIVED = "received";
export const INBOX_MESSAGE_TYPE_RECEIVED_REPLY = "received_reply";
export const INBOX_MESSAGE_TYPE_SENT_SEQUENCE = "sent_sequence";
export const INBOX_MESSAGE_TYPE_SENT_REPLY = "sent_reply";
export const INBOX_MESSAGE_TYPE_SENT_COMPOSE = "sent_compose";
export const INBOX_MESSAGE_TYPE_DRAFT_SEQUENCE = "draft_sequence";
export const INBOX_MESSAGE_TYPE_DRAFT_REPLY = "draft_reply";
export const INBOX_MESSAGE_TYPE_DRAFT_COMPOSE = "draft_compose";
export const INBOX_MESSAGE_TYPE_WARMUP = "warmup";
export const INBOX_MESSAGE_TYPE_BOUNCED = "bounced";

// Custom tracking domain status constants
export const TRACKING_DOMAIN_STATUS_PENDING = "pending";
export const TRACKING_DOMAIN_STATUS_VERIFIED = "verified";
export const TRACKING_DOMAIN_STATUS_FAILED = "failed";
export const DEFAULT_DOMAIN_LIMIT = 3;

// Message status constants
export const INBOX_MESSAGE_STATUS_DRAFT = "draft";
export const INBOX_MESSAGE_STATUS_SENT = "sent";
export const INBOX_MESSAGE_STATUS_RECEIVED = "received";

// Content status constants
export const CONTENT_STATUS_IDEATED = "ideated";
export const CONTENT_STATUS_GENERATING = "generating";
export const CONTENT_STATUS_DRAFT = "draft";
export const CONTENT_STATUS_SCHEDULED = "scheduled";
export const CONTENT_STATUS_PUBLISHED = "published";
export const CONTENT_STATUS_ARCHIVED = "archived";

// Sequence member status types
export const SEQUENCE_MEMBER_STATUS_ACTIVE = "active";
export const SEQUENCE_MEMBER_STATUS_UNSUBSCRIBED = "unsubscribed";
export const SEQUENCE_MEMBER_STATUS_BOUNCED = "bounced";
export const SEQUENCE_MEMBER_STATUS_PAUSED = "paused";
export const SEQUENCE_MEMBER_STATUS_REMOVED = "removed";

// Icon types for list actions
export type ListActionIconType = "mui" | "logo";

export interface ListAction {
  title: string;
  type: string;
  description: string;
  icon: {
    type: ListActionIconType;
    name: string; // MUI icon name or logo key from INTEGRATION_LOGOS
  };
  options: {
    actionParameters: Record<string, unknown>;
  };
  // When set, this menu entry creates a LIST_HEADER_TYPE_LOOKUP column
  // via POST /lists/:id/lookup instead of opening the action-config
  // dialog. No conditions, no actionParameters beyond lookupType.
  lookupType?: string;
}

export const LIST_ACTIONS: ListAction[] = [
  {
    title: "AI Web Scraper: Research Agent",
    type: "agent",
    description:
      "Run an AI agent that can browse the web, research information, and fill in data for your list. Perfect for finding company info, checking career pages, or gathering competitive intelligence.",
    icon: { type: "mui", name: "SmartToy" },
    options: {
      actionParameters: {
        name: "",
        actionType: "agent",
        action: "update_rows",
        model: defaultModel,
        goal: `## Lead Data
\`\`\`json
{{Full Record}}
\`\`\`

You are a Lead Enrichment Specialist AI agent. Your primary task is to enhance lead profiles by discovering and validating additional information based on the initial data provided.

Your Tasks:
1. Analyze the provided lead information
2. Search publicly available sources to find and verify
3. Use the tools to update the lead data
4. Make sure to set isAgency to Yes or No this is an important field. You must set this field with the data you have available.`,
      },
    },
  },
  {
    title: "Add to Sequence",
    type: "add_sequence",
    description:
      "Automatically enroll matching rows into an email or LinkedIn outreach sequence. Start nurturing leads without manual work - rows will begin receiving messages immediately.",
    icon: { type: "mui", name: "Timeline" },
    options: {
      actionParameters: {
        name: "Add to Sequence",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "add_sequence",
      },
    },
  },
  {
    title: "Sequence Status",
    type: LOOKUP_TYPE_SEQUENCE_STATUS,
    description:
      "Show each contact's current status in the most recent sequence they've been added to (Active, Paused, Unsubscribed, Bounced, Removed, or Completed). Computed live — no writes, no stale data.",
    icon: { type: "mui", name: "Timeline" },
    lookupType: LOOKUP_TYPE_SEQUENCE_STATUS,
    options: {
      actionParameters: {},
    },
  },
  {
    title: "HTTP Request",
    type: "http_request",
    description:
      "Send API requests to external services for each row. Connect your list to any tool with an API - enrich data, sync to CRMs, trigger Zapier/Make webhooks, or send to custom tools.",
    icon: { type: "mui", name: "Http" },
    options: {
      actionParameters: {
        name: "HTTP Request",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "http_request",
        method: "POST",
        url: "",
        queryParams: "",
        body: "",
        headers: "{}",
      },
    },
  },
  {
    title: "Add to Smart List",
    type: "add_to_list",
    description:
      "Copy or move rows to another list based on conditions. Organize and segment your data automatically - move qualified leads, archive processed rows, or create targeted sub-lists.",
    icon: { type: "mui", name: "PlaylistAdd" },
    options: {
      actionParameters: {
        name: "Add to Smart List",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "add_to_list",
        targetListId: "",
        removeFromCurrent: false,
      },
    },
  },
  {
    title: "Add Tags",
    type: LIST_ACTION_TYPE_ADD_TAGS,
    description:
      "Apply tags to rows based on conditions. Define multiple tag groups - each group can independently add or remove tags when its conditions match. All groups share the same global Tags column on the row.",
    icon: { type: "mui", name: "LocalOffer" },
    options: {
      actionParameters: {
        name: "Add Tags",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: LIST_ACTION_TYPE_ADD_TAGS,
        tagGroups: [
          {
            mode: LIST_ADD_TAGS_MODE_ADD,
            tags: [],
            conditions: { combinator: "AND", filters: [], groups: [] },
          },
        ],
      },
    },
  },
  {
    title: "AI Text Generator",
    type: "ai_text",
    description:
      "Generate personalized text content using AI for each row. Great for writing custom email intros, value propositions, talking points, or reformatting existing data at scale.",
    icon: { type: "mui", name: "AutoAwesome" },
    options: {
      actionParameters: {
        name: "",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "ai_text",
        model: defaultModel,
        prompt: "",
      },
    },
  },
  {
    title: "AI Lead Scoring",
    type: "ai_text",
    description:
      "Score leads 0-100 based on their fit and likelihood to convert. Uses AI to analyze each row's data and output a numeric score you can use for prioritization and segmentation.",
    icon: { type: "mui", name: "BarChart" },
    options: {
      actionParameters: {
        name: "Lead Score",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "ai_text",
        model: defaultModel,
        prompt: `Analyze the following lead data and score them from 0-100 based on their likelihood to be a qualified prospect. Use these scoring criteria:

EMAIL QUALITY (High Impact):
- Company/business email domain (+20-30 points) - e.g., john@acme.com
- Public/free email domain (-15-25 points) - e.g., gmail.com, yahoo.com, hotmail.com, outlook.com
- Invalid or missing email (-10 points)

JOB TITLE & SENIORITY (High Impact):
- C-Suite (CEO, CFO, CTO, CMO, COO) (+25-30 points)
- VP/Director level (+20-25 points)
- Manager/Head of (+15-20 points)
- Senior individual contributor (+10-15 points)
- Entry level/Intern (+0-5 points)
- Missing or unclear title (-5 points)

COMPANY SIGNALS:
- Company size aligns with target market (+10-15 points)
- Recognized/established company (+5-10 points)
- Has company website or LinkedIn (+5 points)
- Missing company info (-10 points)

DATA COMPLETENESS:
- Has phone number (+5 points)
- Has LinkedIn URL (+5 points)
- Has company name (+5 points)
- Has location/address (+3 points)
- Multiple missing fields (-5 to -15 points)

Lead data:
{{Full Record}}

{{Company Description}}

IMPORTANT: Respond with ONLY a single number between 0 and 100. No explanation, no text, no punctuation - just the numeric score.`,
      },
    },
  },
  {
    title: "AI Image Generator",
    type: "ai_image",
    description:
      "Generate custom images using AI (GPT Image, Leonardo AI, or Google) for each row. Create personalized graphics, product mockups with company branding, or custom social media images.",
    icon: { type: "mui", name: "Image" },
    options: {
      actionParameters: {
        name: "",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "ai_image",
        model: "gpt-image-1",
        prompt: "",
        size: "1024x1024",
        quality: "medium",
      },
    },
  },
  {
    title: "AI Audio Generator",
    type: "ai_audio",
    description:
      "Convert text to natural-sounding speech using ElevenLabs voices. Create personalized voicemail drops, audio messages for video prospecting, or podcast-style content for each contact.",
    icon: { type: "mui", name: "GraphicEq" },
    options: {
      actionParameters: {
        name: "",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "ai_audio",
        model: DEFAULT_AUDIO_MODEL,
        prompt: "",
        voice: DEFAULT_AUDIO_VOICE,
        speed: DEFAULT_AUDIO_SPEED,
      },
    },
  },
  {
    title: "Browser Task",
    type: "browser_task",
    description:
      "Automate browser actions to interact with websites. The AI controls a real browser to navigate, click, and extract data - perfect for pages requiring interaction or login.",
    icon: { type: "logo", name: "browser_session" },
    options: {
      actionParameters: {
        name: "",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: "browser_task",
        task: "",
        sessionType: "none",
        browserIntegrationId: null,
        zipcode: null,
      },
    },
  },
  {
    title: "Sync to CRM",
    type: LIST_ACTION_TYPE_CRM_SYNC,
    description:
      "Sync row data to your connected CRM (HubSpot, GoHighLevel). Automatically create or update contacts with lead information including name, email, phone, company, and LinkedIn URL.",
    icon: { type: "mui", name: "Sync" },
    options: {
      actionParameters: {
        name: "Sync to CRM",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: LIST_ACTION_TYPE_CRM_SYNC,
        crmIntegrationId: "",
      },
    },
  },
  {
    title: "CRM Lookup",
    type: LIST_ACTION_TYPE_CRM_LOOKUP,
    description:
      "Check if a contact already exists in your CRM based on email, phone, or name + company. Useful for deduplication and identifying existing relationships before outreach.",
    icon: { type: "mui", name: "Search" },
    options: {
      actionParameters: {
        name: "CRM Lookup",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: LIST_ACTION_TYPE_CRM_LOOKUP,
        crmIntegrationId: "",
      },
    },
  },
  {
    title: "Apollo Enrichment",
    type: LIST_ACTION_TYPE_APOLLO_ENRICH,
    description:
      "Enrich person data using Apollo.io. Automatically fill in missing fields like email, phone, company info, and social profiles based on first name, last name, and company name.",
    icon: { type: "logo", name: "apollo" },
    options: {
      actionParameters: {
        name: "Apollo Enrich",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: LIST_ACTION_TYPE_APOLLO_ENRICH,
        apolloIntegrationId: "",
        revealPersonalEmails: false,
        revealPhoneNumbers: false,
      },
    },
  },
  {
    title: "Findymail Enrichment",
    type: LIST_ACTION_TYPE_FINDYMAIL_ENRICH,
    description:
      "Enrich leads using Findymail. Automatically enrich leads with verified business emails and additional data based on name and company domain or LinkedIn URL.",
    icon: { type: "logo", name: "findymail" },
    options: {
      actionParameters: {
        name: "Findymail Enrich",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: LIST_ACTION_TYPE_FINDYMAIL_ENRICH,
        findymailIntegrationId: "",
      },
    },
  },
  {
    title: "Better Contact Enrichment",
    type: LIST_ACTION_TYPE_BETTERCONTACT_ENRICH,
    description:
      "Enrich contacts with verified email addresses and phone numbers using Better Contact's waterfall enrichment. Uses 20+ data providers to maximize match rates.",
    icon: { type: "logo", name: "bettercontact" },
    options: {
      actionParameters: {
        name: "Better Contact Enrich",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: LIST_ACTION_TYPE_BETTERCONTACT_ENRICH,
        bettercontactIntegrationId: "",
        enrichEmail: true,
        enrichPhone: false,
      },
    },
  },
  {
    title: "Audience Enrichment",
    type: LIST_ACTION_TYPE_AUDIENCELAB_ENRICH,
    description:
      "Enrich contacts with up to 75 fields including email, phone, job title, company info, demographics, and more based on name, email, LinkedIn URL, or other identifiers.",
    icon: { type: "mui", name: "AdsClick" },
    options: {
      actionParameters: {
        name: "Audience Enrich",
        action: LIST_ACTION_TYPE_UPDATE_ROWS,
        actionType: LIST_ACTION_TYPE_AUDIENCELAB_ENRICH,
      },
    },
  },
];

export const COMPANY_SIZES = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "5001-10,000",
  "10,001-50,000",
  "50,001-100,000",
  "100,000+",
];

export const INDUSTRIES = [
  "Aerospace",
  "Agriculture",
  "Automotive",
  "Biotechnology",
  "Chemicals",
  "Consulting",
  "Construction",
  "Defense",
  "Education",
  "Electronics",
  "Energy",
  "Fashion",
  "Finance",
  "Food & Beverage",
  "Government",
  "Healthcare",
  "Hospitality",
  "Insurance",
  "Legal",
  "Logistics",
  "Manufacturing",
  "Media & Entertainment",
  "Mining",
  "Music",
  "Non-Profit",
  "Pharmaceuticals",
  "Printing",
  "Publishing",
  "Real Estate",
  "Recycling",
  "Retail",
  "Sports",
  "Technology",
  "Telecommunications",
  "Textiles",
  "Transportation",
  "Utilities",
  "Venture Capital",
];

// Agent channel type constants
export const AGENT_CHANNEL_WIDGET = "Widget"; // Legacy - use WEBSITE instead
export const AGENT_CHANNEL_WEBPAGE = "Webpage"; // Shareable agent page (/a/{code})
export const AGENT_CHANNEL_WEBSITE = "Website"; // Embedded chat widget on external websites
export const AGENT_CHANNEL_EMAIL = "Email";
export const AGENT_CHANNEL_SMS = "SMS";
export const AGENT_CHANNEL_VOICE = "Voice";
export const AGENT_CHANNEL_OUTBOUND_SMS = "Outbound SMS"; // Outbound agent SMS conversations (sequences)
export const AGENT_CHANNEL_OUTBOUND_VOICE = "Outbound Voice"; // Outbound agent voice calls (sequences)

// Agent conversation message types
export const AGENT_MESSAGE_TYPE_USER = "user";
export const AGENT_MESSAGE_TYPE_AGENT = "agent";
export const AGENT_MESSAGE_TYPE_DRAFT_AGENT = "draft_agent";

// Agent conversation message statuses
export const AGENT_MESSAGE_STATUS_DRAFT = "draft";
export const AGENT_MESSAGE_STATUS_SENT = "sent";
export const AGENT_MESSAGE_STATUS_RECEIVED = "received";

// Image provider display mapping - maps internal provider names to user-friendly names
export const IMAGE_PROVIDER_DISPLAY_MAPPING: Record<string, string> = {
  openai: "OpenAI",
  leonardo: "Leonardo AI",
  google: "Google",
  anthropic: "Anthropic",
};

// Document upload file type constants
// Based on backend processFile function in documents_helper.py
export const ALLOWED_DOCUMENT_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".csv",
  ".xls",
  ".xlsx",
  ".m4a",
  ".mp3",
  ".webm",
  ".mp4",
  ".txt",
  ".md",
  ".json",
  ".html",
] as const;

// Document upload size limits (must match backend MAX_DOCUMENT_SIZE_MB)
export const MAX_DOCUMENT_SIZE_MB = 50;

// Premium PDF upload credit cost (must match backend PREMIUM_PDF_CREDIT_COST)
export const PREMIUM_PDF_CREDIT_COST = 5;

export const DOCUMENT_FILE_TYPES = {
  DOCUMENTS: [".pdf", ".doc", ".docx"],
  SPREADSHEETS: [".csv", ".xls", ".xlsx"],
  AUDIO: [".m4a", ".mp3", ".webm", ".mp4"],
  TEXT: [".txt", ".md", ".json", ".html"],
} as const;

// HTML accept attribute value for document file inputs
export const DOCUMENT_ACCEPT_TYPES = ALLOWED_DOCUMENT_EXTENSIONS.join(",");

// Validate that a file has an allowed document extension
export const isAllowedDocumentType = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return ALLOWED_DOCUMENT_EXTENSIONS.some(ext => fileName.endsWith(ext));
};

// User-friendly display names for file types
export const DOCUMENT_TYPE_LABELS = {
  ".pdf": "PDF",
  ".doc": "Word Document",
  ".docx": "Word Document",
  ".csv": "CSV Spreadsheet",
  ".xls": "Excel Spreadsheet",
  ".xlsx": "Excel Spreadsheet",
  ".m4a": "Audio File",
  ".mp3": "Audio File",
  ".webm": "Audio File",
  ".mp4": "Audio File",
  ".txt": "Text File",
  ".md": "Markdown File",
  ".json": "JSON File",
  ".html": "HTML File",
} as const;

// Service Package Types (must match backend SERVICE_PACKAGE_TYPE_* constants)
export const SERVICE_PACKAGE_TYPE_ONBOARDING = "onboarding";
export const SERVICE_PACKAGE_TYPE_SETUP = "setup";
export const SERVICE_PACKAGE_TYPE_MARKETING = "marketing";
export const SERVICE_PACKAGE_TYPE_TRAINING = "training";
export const SERVICE_PACKAGE_TYPE_CUSTOM = "custom";
export const SERVICE_PACKAGE_TYPE_ADDON = "addon";
export const SERVICE_PACKAGE_TYPE_COMPANIES = "companies";
export const SERVICE_PACKAGE_TYPE_SEATS = "seats";
export const SERVICE_PACKAGE_TYPE_CREDITS = "credits";
export const SERVICE_PACKAGE_TYPE_AUDIENCE = "audience";
export const SERVICE_PACKAGE_TYPE_PIXEL = "pixel";

// Service Package Billing Types (must match backend)
export const SERVICE_PACKAGE_BILLING_TYPE_ONE_TIME = "one_time";
export const SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION = "subscription";

// Service Package Billing Intervals (must match backend)
export const SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY = "monthly";
export const SERVICE_PACKAGE_BILLING_INTERVAL_ANNUAL = "annual";

// Service Package Order Statuses (must match backend)
export const SERVICE_PACKAGE_ORDER_STATUS_PENDING = "pending";
export const SERVICE_PACKAGE_ORDER_STATUS_PAID = "paid";
export const SERVICE_PACKAGE_ORDER_STATUS_FULFILLED = "fulfilled";
export const SERVICE_PACKAGE_ORDER_STATUS_REFUNDED = "refunded";
export const SERVICE_PACKAGE_ORDER_STATUS_CANCELLED = "cancelled";
export const SERVICE_PACKAGE_ORDER_STATUS_ACTIVE = "active";
export const SERVICE_PACKAGE_ORDER_STATUS_CANCELED = "canceled";

// Service package type display names
export const SERVICE_PACKAGE_TYPE_LABELS: Record<string, string> = {
  [SERVICE_PACKAGE_TYPE_ONBOARDING]: "Onboarding",
  [SERVICE_PACKAGE_TYPE_SETUP]: "Setup",
  [SERVICE_PACKAGE_TYPE_MARKETING]: "Marketing",
  [SERVICE_PACKAGE_TYPE_TRAINING]: "Training",
  [SERVICE_PACKAGE_TYPE_CUSTOM]: "Custom",
  [SERVICE_PACKAGE_TYPE_ADDON]: "Add-on",
  [SERVICE_PACKAGE_TYPE_COMPANIES]: "Additional Companies",
  [SERVICE_PACKAGE_TYPE_SEATS]: "Additional Seats",
  [SERVICE_PACKAGE_TYPE_CREDITS]: "Credit Package",
  [SERVICE_PACKAGE_TYPE_AUDIENCE]: "Audience Builder",
  [SERVICE_PACKAGE_TYPE_PIXEL]: "Website Pixel",
} as const;

// Activity Log - Asset Types (must match backend ACTIVITY_ASSET_TYPE_* constants)
export const ACTIVITY_ASSET_TYPE_USER = "user";
export const ACTIVITY_ASSET_TYPE_EMPLOYEE = "employee";
export const ACTIVITY_ASSET_TYPE_DOCUMENT = "document";
export const ACTIVITY_ASSET_TYPE_COMPANY = "company";
export const ACTIVITY_ASSET_TYPE_CHAT = "chat";
export const ACTIVITY_ASSET_TYPE_INTEGRATION = "integration";
export const ACTIVITY_ASSET_TYPE_WORKFLOW = "workflow";
export const ACTIVITY_ASSET_TYPE_SEQUENCE = "sequence";
export const ACTIVITY_ASSET_TYPE_LIST = "list";

// Activity Log - Action Types (must match backend ACTIVITY_ACTION_* constants)
export const ACTIVITY_ACTION_LOGIN = "login";
export const ACTIVITY_ACTION_LOGIN_FAILED = "login_failed";
export const ACTIVITY_ACTION_LOGOUT = "logout";
export const ACTIVITY_ACTION_MFA_SETUP = "mfa_setup";
export const ACTIVITY_ACTION_MFA_ENABLED = "mfa_enabled";
export const ACTIVITY_ACTION_MFA_DISABLED = "mfa_disabled";
export const ACTIVITY_ACTION_MFA_VERIFY = "mfa_verify";
export const ACTIVITY_ACTION_MFA_VERIFY_FAILED = "mfa_verify_failed";
export const ACTIVITY_ACTION_CREATE = "create";
export const ACTIVITY_ACTION_UPDATE = "update";
export const ACTIVITY_ACTION_DELETE = "delete";
export const ACTIVITY_ACTION_VIEW = "view";
export const ACTIVITY_ACTION_SEARCH = "search";
export const ACTIVITY_ACTION_ACCESS = "access";
export const ACTIVITY_ACTION_QUERY = "query";
export const ACTIVITY_ACTION_DOWNLOAD = "download";
export const ACTIVITY_ACTION_SHARE = "share";

// Activity Log - Display labels for action types
export const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  [ACTIVITY_ACTION_LOGIN]: "Login",
  [ACTIVITY_ACTION_LOGIN_FAILED]: "Login Failed",
  [ACTIVITY_ACTION_LOGOUT]: "Logout",
  [ACTIVITY_ACTION_MFA_SETUP]: "MFA Setup",
  [ACTIVITY_ACTION_MFA_ENABLED]: "MFA Enabled",
  [ACTIVITY_ACTION_MFA_DISABLED]: "MFA Disabled",
  [ACTIVITY_ACTION_MFA_VERIFY]: "MFA Verified",
  [ACTIVITY_ACTION_MFA_VERIFY_FAILED]: "MFA Verification Failed",
  [ACTIVITY_ACTION_CREATE]: "Created",
  [ACTIVITY_ACTION_UPDATE]: "Updated",
  [ACTIVITY_ACTION_DELETE]: "Deleted",
  [ACTIVITY_ACTION_VIEW]: "Viewed",
  [ACTIVITY_ACTION_SEARCH]: "Searched",
  [ACTIVITY_ACTION_ACCESS]: "Accessed",
  [ACTIVITY_ACTION_QUERY]: "Queried",
  [ACTIVITY_ACTION_DOWNLOAD]: "Downloaded",
  [ACTIVITY_ACTION_SHARE]: "Shared",
} as const;

// Activity Log - Display labels for asset types
export const ACTIVITY_ASSET_TYPE_LABELS: Record<string, string> = {
  [ACTIVITY_ASSET_TYPE_USER]: "User",
  [ACTIVITY_ASSET_TYPE_EMPLOYEE]: "Employee",
  [ACTIVITY_ASSET_TYPE_DOCUMENT]: "Document",
  [ACTIVITY_ASSET_TYPE_COMPANY]: "Company",
  [ACTIVITY_ASSET_TYPE_CHAT]: "Chat",
  [ACTIVITY_ASSET_TYPE_INTEGRATION]: "Integration",
  [ACTIVITY_ASSET_TYPE_WORKFLOW]: "Workflow",
  [ACTIVITY_ASSET_TYPE_SEQUENCE]: "Sequence",
  [ACTIVITY_ASSET_TYPE_LIST]: "List",
} as const;

// Folder sharing type constants
export const FOLDER_SHARE_TYPE_COMPANY = "company";
export const FOLDER_SHARE_TYPE_DIRECT = "direct";
export const FOLDER_SHARE_TYPE_PUBLIC = "public";

// Share invitation status constants
export const SHARE_INVITATION_STATUS_PENDING = "pending";
export const SHARE_INVITATION_STATUS_ACCEPTED = "accepted";
export const SHARE_INVITATION_STATUS_DECLINED = "declined";

// Company user role constants
export const ROLE_OWNER = "owner";
export const ROLE_ADMIN = "admin";
export const ROLE_MEMBER = "member";

// Integration type constants
export const INTEGRATION_TYPE_APOLLO = "apollo";
export const INTEGRATION_TYPE_FINDYMAIL = "findymail";
export const INTEGRATION_TYPE_BETTERCONTACT = "bettercontact";
export const INTEGRATION_TYPE_GITHUB = "github";

// App Builder git sync — provider-agnostic constants
export const GIT_PROVIDER_GITHUB = "github";
export const GIT_SYNC_POLL_INTERVAL_MS = 2000;

// Demo settings localStorage keys - for super users
export const DEMO_MODE_KEY = "demo_mode";
export const HIDE_LOGO_KEY = "hide_logo";

// Brand Voice field length limits (must match backend model constraints)
export const BRAND_VOICE_FIELD_LIMITS = {
  name: 100,          // String(100) in model
  industry: 100,      // String(100) in model
  // Note: audience and description are Text columns with no hard limit,
  // but we set reasonable UX limits
  audience: 2000,
  description: 5000,
  editingInstructions: 5000,  // Custom instructions for content editing
};

// Content Strategy field length limits (Text columns - UX limits)
export const CONTENT_STRATEGY_FIELD_LIMITS = {
  name: 255,
  salesNarrative: 5000,
  targetAudience: 2000,
};

// ICP (Ideal Customer Profile) field length limits
export const ICP_FIELD_LIMITS = {
  // Top-level fields (stored as columns)
  name: 255,
  companySize: 100,
  industry: 1000,
  budget: 500,
  location: 500,
  description: 5000,
  // JSONB nested fields - reasonable limits for UX
  shortText: 200,      // For short single-line fields like persona names, roles
  mediumText: 500,     // For medium text like revenue ranges, growth stage
  longText: 2000,      // For longer text like profile descriptions
  arrayText: 3000,     // For comma-separated arrays that could have many items
};

// Brand Image field length limits (must match backend model constraints)
export const BRAND_IMAGE_FIELD_LIMITS = {
  name: 100,              // String(100) in model
  description: 5000,      // Text column - UX limit
  imageStylePrompt: 2000, // Text column - UX limit for Midjourney-style prompts
  templateName: 100,      // Template name limit
  templateDescription: 500, // Template description limit
};

// Content Campaign field length limits (must match backend model constraints)
export const CAMPAIGN_FIELD_LIMITS = {
  name: 255,              // Text column - UX limit
  plan: 10000,            // Text column - UX limit for detailed campaign plans
  tone: 50,               // String(50) in model
  model: 200,             // String(200) in model
  color: 30,              // String(30) in model
};

// ICP Type constants (B2B vs B2C)
export const ICP_TYPE_B2B = "b2b";
export const ICP_TYPE_B2C = "b2c";
export const ICP_TYPES = [
  { value: ICP_TYPE_B2B, label: "B2B (Business-to-Business)" },
  { value: ICP_TYPE_B2C, label: "B2C (Business-to-Consumer)" },
];

// Meeting tracking link variable - placeholder for calendar booking links in email templates
export const MEETING_TRACKING_LINK_VARIABLE = "meetingTrackingLink";

// AI Snippet variable prefix - used for dynamic AI-generated content at send time
// Format: {{AI_SNIPPET:base64_encoded_prompt}}
export const AI_SNIPPET_VARIABLE_PREFIX = "AI_SNIPPET";

// Message length limits for sequences
export const MESSAGE_LENGTH_LIMITS = {
  // SMS: Standard SMS is 160 characters, but longer messages can be split
  SMS_SINGLE: 160,
  SMS_MAX: 1600, // 10 segments max is reasonable

  // LinkedIn connection request note limit
  LINKEDIN_CONNECTION_REQUEST: 300,

  // LinkedIn message limit (regular messages)
  LINKEDIN_MESSAGE: 8000,

  // LinkedIn InMail limit
  LINKEDIN_INMAIL: 1900,
} as const;

// Widget style constants for chat widget button appearance
export const WIDGET_STYLE_DEFAULT = "default";
export const WIDGET_STYLE_AVATAR = "avatar";
export const WIDGET_STYLE_GIF = "gif";

export const WIDGET_STYLES = [
  {
    value: WIDGET_STYLE_DEFAULT,
    label: "Chat Icon",
    description: "A standard chat bubble icon",
  },
  {
    value: WIDGET_STYLE_AVATAR,
    label: "Agent Avatar",
    description: "Display the agent's avatar image",
  },
  {
    value: WIDGET_STYLE_GIF,
    label: "Custom GIF",
    description: "An animated GIF for an interactive look",
  },
];

// PII Obfuscation - Local Storage key for persisting obfuscation state
export const PII_OBFUSCATION_STORAGE_KEY = "pii_obfuscation_enabled";

// PII-sensitive column data types (from ListColumnTypeDialog)
export const PII_DATA_TYPES = ["email", "phone", "linkedin_url"] as const;

// Column name patterns that indicate PII (case-insensitive matching)
export const PII_COLUMN_NAME_PATTERNS = {
  // Full name fields - obfuscate entirely except first char
  fullName: ["full_name", "fullname", "contact_name", "contactname"],
  // Last name fields - show first 3 chars (like Apollo)
  lastName: ["last_name", "lastname", "lname", "surname"],
  // Personal/sensitive data fields - blur these
  personal: ["personal", "ip", "ip_address", "ipaddress"],
  // Note: First name is NOT obfuscated
  // Note: "name" alone is not obfuscated (could be company name, product name, etc.)
} as const;

// Scheduled Task Types
export const SCHEDULED_TASK_TYPE_ONE_TIME = "one_time";
export const SCHEDULED_TASK_TYPE_RECURRING = "recurring";

// Scheduled Task Status
export const SCHEDULED_TASK_STATUS_ACTIVE = "active";
export const SCHEDULED_TASK_STATUS_PAUSED = "paused";
export const SCHEDULED_TASK_STATUS_COMPLETED = "completed";

// Scheduled Task Execution Status
export const SCHEDULED_TASK_EXECUTION_STATUS_PENDING = "pending";
export const SCHEDULED_TASK_EXECUTION_STATUS_RUNNING = "running";
export const SCHEDULED_TASK_EXECUTION_STATUS_COMPLETED = "completed";
export const SCHEDULED_TASK_EXECUTION_STATUS_FAILED = "failed";

// Recurring Frequency Options
export const SCHEDULED_TASK_FREQUENCY_DAILY = "daily";
export const SCHEDULED_TASK_FREQUENCY_WEEKLY = "weekly";
export const SCHEDULED_TASK_FREQUENCY_MONTHLY = "monthly";

// Days of week for recurring tasks (Sunday=0 format)
export const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Common timezones for scheduled task selection
export const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Australia/Sydney",
  "UTC",
];

// =============================================================================
// REMOTE AGENT CONSTANTS
// =============================================================================

// Remote Agent Status
export const REMOTE_AGENT_STATUS_ONLINE = "online";
export const REMOTE_AGENT_STATUS_OFFLINE = "offline";
export const REMOTE_AGENT_STATUS_BUSY = "busy";

// Remote Agent Command Status
export const REMOTE_AGENT_COMMAND_STATUS_PENDING = "pending";
export const REMOTE_AGENT_COMMAND_STATUS_RUNNING = "running";
export const REMOTE_AGENT_COMMAND_STATUS_COMPLETED = "completed";
export const REMOTE_AGENT_COMMAND_STATUS_FAILED = "failed";
export const REMOTE_AGENT_COMMAND_STATUS_CANCELLED = "cancelled";

// Executive Assistant Skill Sources
export const SKILL_SOURCE_CLAWHUB = "clawhub";
export const SKILL_SOURCE_CUSTOM = "custom";

// Executive Assistant Skill Status
export const SKILL_STATUS_ACTIVE = "active";
export const SKILL_STATUS_DISABLED = "disabled";

// Filesystem tree node structure
export interface FilesystemNode {
  name: string;
  path: string;
  type: "directory" | "file";
  size?: number; // bytes, only for files
  modified?: string; // ISO timestamp
  children?: FilesystemNode[]; // only for directories
  truncated?: boolean; // true if directory listing was truncated due to depth limit
  error?: string; // error message if listing failed
}

// Remote Agent interface
export interface RemoteAgent {
  id: string;
  userId: string;
  companyId: string | null;
  name: string;
  description: string | null;
  hostname: string | null;
  machineId: string | null;
  apiKey?: string; // Only included on creation
  osType: string | null;
  osVersion: string | null;
  architecture: string | null;
  pythonVersion: string | null;
  agentVersion: string | null;
  ipAddress: string | null;
  publicIp: string | null;
  status: "online" | "offline" | "busy";
  lastHeartbeat: string | null;
  lastCommandAt: string | null;
  tags: string[];
  agentMetadata: Record<string, unknown>;
  workspacePath: string | null;
  filesystemTreeUpdatedAt: string | null;
  createdDate: string;
  updatedDate: string | null;
}

// Project file export formats
export const EXPORT_FORMAT_PDF = "pdf";
export const EXPORT_FORMAT_DOCX = "docx";
export const EXPORT_FORMAT_CSV = "csv";

// Keyword Monitor Status
export const KEYWORD_MONITOR_STATUS_ACTIVE = "active";
export const KEYWORD_MONITOR_STATUS_PAUSED = "paused";

// Keyword Monitor Platforms
export const KEYWORD_MONITOR_PLATFORM_REDDIT = "reddit";
export const KEYWORD_MONITOR_PLATFORM_HACKERNEWS = "hackernews";
export const KEYWORD_MONITOR_PLATFORM_LINKEDIN = "linkedin";
export const KEYWORD_MONITOR_PLATFORM_X = "x";
export const KEYWORD_MONITOR_PLATFORM_QUORA = "quora";
export const KEYWORD_MONITOR_PLATFORM_CHATGPT = "chatgpt";
export const KEYWORD_MONITOR_PLATFORM_GOOGLE_AI = "google_ai";

// Monitor Check Frequency
export const KEYWORD_MONITOR_FREQUENCY_HOURLY = "hourly";
export const KEYWORD_MONITOR_FREQUENCY_3H = "3h";
export const KEYWORD_MONITOR_FREQUENCY_6H = "6h";
export const KEYWORD_MONITOR_FREQUENCY_12H = "12h";
export const KEYWORD_MONITOR_FREQUENCY_DAILY = "daily";
export const KEYWORD_MONITOR_FREQUENCY_WEEKLY = "weekly";

// Mention Status
export const MENTION_STATUS_NEW = "new";
export const MENTION_STATUS_SEEN = "seen";
export const MENTION_STATUS_DISMISSED = "dismissed";
export const MENTION_STATUS_ACTIVE = "active"; // Filter: excludes dismissed

// Reply Styles
export const REPLY_STYLE_VALUE_FIRST = "value-first";
export const REPLY_STYLE_EDUCATIONAL = "educational";
export const REPLY_STYLE_CONVERSATIONAL = "conversational";
export const REPLY_STYLE_PROBLEM_SOLVER = "problem-solver";

// Reply Style Options for UI
export const REPLY_STYLE_OPTIONS = [
  {
    value: REPLY_STYLE_VALUE_FIRST,
    label: "Value-First",
    description: "Lead with helpful information, subtle product mention",
  },
  {
    value: REPLY_STYLE_EDUCATIONAL,
    label: "Educational",
    description: "Share knowledge and expertise, position as thought leader",
  },
  {
    value: REPLY_STYLE_CONVERSATIONAL,
    label: "Conversational",
    description: "Friendly and casual engagement",
  },
  {
    value: REPLY_STYLE_PROBLEM_SOLVER,
    label: "Problem Solver",
    description: "Direct solution to the user's problem",
  },
];

// Marketing Campaign statuses (must match backend constants.py)
export const MARKETING_CAMPAIGN_STATUS_DRAFT = "draft";
export const MARKETING_CAMPAIGN_STATUS_SCHEDULED = "scheduled";
export const MARKETING_CAMPAIGN_STATUS_SENDING = "sending";
export const MARKETING_CAMPAIGN_STATUS_SENT = "sent";
export const MARKETING_CAMPAIGN_STATUS_FAILED = "failed";
export const MARKETING_CAMPAIGN_STATUS_CANCELLED = "cancelled";

export const MARKETING_CAMPAIGN_SCHEDULE_NOW = "now";
export const MARKETING_CAMPAIGN_SCHEDULE_LATER = "scheduled";

// Reserved placeholder inside a marketing campaign template. Replaced in the
// browser when a user applies a template to a new campaign.
export const MARKETING_TEMPLATE_CONTENT_TOKEN = "{{content}}";

// Marketing Campaign Template builder editor modes.
export const MARKETING_TEMPLATE_EDITOR_WYSIWYG = "wysiwyg";
export const MARKETING_TEMPLATE_EDITOR_AI_CHAT = "ai-chat";

export const MARKETING_RECIPIENT_STATUS_PENDING = "pending";
export const MARKETING_RECIPIENT_STATUS_SENT = "sent";
export const MARKETING_RECIPIENT_STATUS_FAILED = "failed";
export const MARKETING_RECIPIENT_STATUS_SKIPPED = "skipped";

// Remote Agent Command interface
export interface RemoteAgentCommand {
  id: string;
  userId: string;
  companyId: string | null;
  agentId: string;
  createdBy: string | null;
  commandType: "shell" | "script";
  command: string;
  workingDirectory: string | null;
  environment: Record<string, string>;
  timeout: number;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  priority: number;
  exitCode: number | null;
  stdout: string | null;
  stderr: string | null;
  errorMessage: string | null;
  queuedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
}
